/**
 * V3 homepage editor — TREATX-V3-CMS-GAP-01
 * =========================================
 * The SAME Landing CMS authority and the SAME endpoints the rest of Website
 * Management uses (`/api/admin/landing-content`); this is a section-by-section
 * VIEW of those records, not a second CMS and not a second storage.
 *
 *   • A section's copy      → one `section_copy` record, `section_key` = section
 *   • The hero headline     → the `hero_content` record scoped to V3
 *   • A section's list items→ `feature_content` records sharing the section_key
 *   • Section images/scenes → Product Visuals panel (unchanged authority)
 *
 * Every record created here is scoped `homepage_versions: ["V3"]`, so nothing
 * on the published V1 homepage can be affected. A section with no record keeps
 * rendering its built-in default, which is shown as the field placeholder and
 * can be adopted with one click.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Loader2, Save, Plus, Trash2, Eye, EyeOff, ChevronDown, ChevronRight, Check,
} from 'lucide-react';
import { MultilingualInput } from '../../components/MultilingualInput';
import { getLocalizedField } from '../../context/LanguageContext';
import api from '../../utils/api';
import { ProductVisualsPanel } from './ProductVisualsPanel';
import { ImageUploader } from './AdminLandingCMS';
import { V3_SECTIONS, defaultsFor, itemDefaultsFor } from './v3SectionDefaults';
import { IconPicker } from './IconPicker';
import { SectionOrderPanel } from './SectionOrderPanel';
import { DEFAULT_ITEM_ICON } from '../../lib/v3Icons';

const ml = (v) => (typeof v === 'string' ? { en: v } : (v || {}));
const en = (v) => getLocalizedField(v, 'en') || '';

const FieldRow = ({ children }) => <div className="grid gap-3 sm:grid-cols-2">{children}</div>;

// ── one managed section ───────────────────────────────────
const SectionCard = ({ def, record, items, destinations, onCreate, onSave, onToggle,
                       onItemSave, onItemDelete, busy }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const defaults = defaultsFor(def.key);

  useEffect(() => {
    setForm({
      eyebrow: ml(record?.eyebrow), title: ml(record?.title),
      description: ml(record?.description), badge_label: ml(record?.badge_label),
      footnote: ml(record?.footnote), link_label: ml(record?.link_label),
      cta_label: ml(record?.cta_label), cta_href: record?.cta_href || '',
      cta_label_secondary: ml(record?.cta_label_secondary),
      cta_href_secondary: record?.cta_href_secondary || '',
      display_order: record?.display_order ?? def.order,
    });
    setDirty(false);
  }, [record, def.key, def.order]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const managed = !!record;

  return (
    <Card className={`border ${managed ? 'border-slate-200' : 'border-dashed border-slate-300'}`}
          data-testid={`v3-section-${def.key}`}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setOpen((o) => !o)}
                  className="flex flex-1 items-center gap-2 text-left"
                  data-testid={`v3-section-toggle-${def.key}`}>
            {open ? <ChevronDown className="h-4 w-4 text-slate-400" />
                  : <ChevronRight className="h-4 w-4 text-slate-400" />}
            <span className="text-sm font-semibold text-slate-900">{def.label}</span>
            <Badge variant="outline" className="text-[10px]">V3</Badge>
            {managed ? (
              <Badge className="bg-emerald-100 text-[10px] text-emerald-700"
                     data-testid={`v3-section-managed-${def.key}`}>Managed</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-slate-500"
                     data-testid={`v3-section-default-${def.key}`}>Built-in default</Badge>
            )}
            {managed && record.active === false && (
              <Badge variant="outline" className="text-[10px] text-amber-600">Hidden on the page</Badge>
            )}
          </button>
          {managed ? (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                    onClick={() => onToggle(record)}
                    data-testid={`v3-section-visibility-${def.key}`}>
              {record.active === false ? <EyeOff className="h-4 w-4 text-slate-400" />
                                       : <Eye className="h-4 w-4 text-emerald-500" />}
            </Button>
          ) : (
            <Button size="sm" className="h-7 text-xs" disabled={busy}
                    onClick={() => onCreate(def)}
                    data-testid={`v3-section-create-${def.key}`}>
              <Plus className="mr-1 h-3 w-3" /> Start managing
            </Button>
          )}
        </div>
        <p className="mt-1 pl-6 text-xs text-slate-500">{def.blurb}</p>

        {open && (
          <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
            {!managed && (
              <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                This section currently shows the built-in copy below. Click
                <span className="font-semibold"> Start managing</span> to copy it into an
                editable record — the page keeps looking exactly the same.
              </p>
            )}
            {def.fields.includes('eyebrow') && (
              <MultilingualInput label="Eyebrow (small label above the heading)"
                value={form.eyebrow} onChange={(v) => set('eyebrow', v)}
                placeholder={defaults.eyebrow?.en} policy="display_fallback_only" />
            )}
            {def.fields.includes('title') && (
              <MultilingualInput label="Heading" value={form.title}
                onChange={(v) => set('title', v)} placeholder={defaults.title?.en}
                policy="translatable_required" />
            )}
            {def.fields.includes('description') && (
              <MultilingualInput label="Description" value={form.description} multiline
                onChange={(v) => set('description', v)} placeholder={defaults.description?.en}
                policy="display_fallback_only" />
            )}
            {def.fields.includes('badge_label') && (
              <MultilingualInput label={def.badgeLabel || 'Status badge'}
                value={form.badge_label} onChange={(v) => set('badge_label', v)}
                placeholder={defaults.badge_label?.en} policy="display_fallback_only" />
            )}
            {def.fields.includes('link_label') && (
              <MultilingualInput label={def.linkLabel || 'In-card link label'}
                value={form.link_label} onChange={(v) => set('link_label', v)}
                placeholder={defaults.link_label?.en} policy="display_fallback_only" />
            )}
            {def.fields.includes('cta') && (
              <FieldRow>
                <MultilingualInput label="Button label" value={form.cta_label}
                  onChange={(v) => set('cta_label', v)} placeholder={defaults.cta_label?.en}
                  policy="display_fallback_only" />
                <div>
                  <Label className="text-xs">Button destination</Label>
                  <select value={form.cta_href || ''} onChange={(e) => set('cta_href', e.target.value)}
                          className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                          data-testid={`v3-cta-href-${def.key}`}>
                    <option value="">— built-in default ({defaults.cta_href || 'none'}) —</option>
                    {destinations.map((d) => (
                      <option key={d.value} value={d.value}>{d.label} — {d.value}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-slate-400">
                    Only existing public pages can be chosen, so a button can never
                    point at a dead link.
                  </p>
                </div>
              </FieldRow>
            )}
            {def.fields.includes('cta_secondary') && (
              <FieldRow>
                <MultilingualInput label="Second button label" value={form.cta_label_secondary}
                  onChange={(v) => set('cta_label_secondary', v)}
                  placeholder={defaults.cta_label_secondary?.en} policy="display_fallback_only" />
                <div>
                  <Label className="text-xs">Second button destination</Label>
                  <select value={form.cta_href_secondary || ''}
                          onChange={(e) => set('cta_href_secondary', e.target.value)}
                          className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                          data-testid={`v3-cta-href2-${def.key}`}>
                    <option value="">— built-in default ({defaults.cta_href_secondary || 'none'}) —</option>
                    {destinations.map((d) => (
                      <option key={d.value} value={d.value}>{d.label} — {d.value}</option>
                    ))}
                  </select>
                </div>
              </FieldRow>
            )}
            {def.ownedElsewhere && (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs leading-relaxed text-amber-900"
                 data-testid={`v3-owned-elsewhere-${def.key}`}>
                <span className="font-semibold">{def.ownedElsewhere.what}</span> of this
                section are edited on the
                <span className="font-semibold"> {def.ownedElsewhere.where} </span>
                tab — that is their single source of truth, so they are not repeated
                here and cannot drift apart.
              </p>
            )}
            {def.fields.includes('cta_href_only') && (
              <div>
                <Label className="text-xs">First button destination</Label>
                <select value={form.cta_href || ''} onChange={(e) => set('cta_href', e.target.value)}
                        className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm sm:w-80"
                        data-testid={`v3-cta-href-${def.key}`}>
                  <option value="">— built-in default ({defaults.cta_href || 'none'}) —</option>
                  {destinations.map((d) => (
                    <option key={d.value} value={d.value}>{d.label} — {d.value}</option>
                  ))}
                </select>
                <p className="mt-1 text-[10px] text-slate-400">
                  The label of this button comes from Provider Benefits; where it
                  takes the visitor is decided here.
                </p>
              </div>
            )}
            {def.fields.includes('footnote') && (
              <MultilingualInput label={def.footnoteLabel || 'Small print under the section'}
                value={form.footnote} onChange={(v) => set('footnote', v)}
                placeholder={defaults.footnote?.en} policy="display_fallback_only" />
            )}

            {managed && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Order</Label>
                  <Input type="number" value={form.display_order}
                         onChange={(e) => set('display_order', parseInt(e.target.value, 10) || 0)}
                         className="h-8 w-20 text-sm" data-testid={`v3-order-${def.key}`} />
                </div>
                <Button size="sm" disabled={busy || !dirty}
                        onClick={() => onSave(record, form)}
                        data-testid={`v3-section-save-${def.key}`}>
                  {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        : <Save className="mr-1 h-3 w-3" />}
                  Save
                </Button>
              </div>
            )}

            {def.visuals && (
              <div className="rounded-lg border border-slate-200 bg-white p-3"
                   data-testid={`v3-visuals-${def.key}`}>
                <ProductVisualsPanel sectionKey={def.visuals} compact />
              </div>
            )}

            {def.items && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-700">{def.items.label}</p>
                  <Button variant="outline" size="sm" className="h-7 text-xs" disabled={busy}
                          onClick={() => onItemSave(null, def, {
                            title: { en: '' }, description: { en: '' },
                            display_order: (items?.length || 0) + 1,
                          })}
                          data-testid={`v3-item-add-${def.key}`}>
                    <Plus className="mr-1 h-3 w-3" /> Add
                  </Button>
                </div>
                <div className="mt-2 space-y-2">
                  {(items || []).map((it) => (
                    <ItemRow key={it.block_id} item={it} def={def} busy={busy}
                             onSave={onItemSave} onDelete={onItemDelete} />
                  ))}
                  {!items?.length && (
                    <p className="py-1 text-[11px] text-slate-500">
                      No managed items — the page shows its built-in {def.items.label.toLowerCase()}.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ItemRow = ({ item, def, busy, onSave, onDelete }) => {
  const [form, setForm] = useState({
    title: ml(item.title), description: ml(item.description),
    display_order: item.display_order ?? 0,
    icon: item.icon || '',
    image_url: item.image_url || '', thumbnail_url: item.thumbnail_url || '',
    compressed_url: item.compressed_url || '',
  });
  const [dirty, setDirty] = useState(false);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2"
         data-testid={`v3-item-${item.block_id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-2">
          <MultilingualInput label="Item title" value={form.title}
            onChange={(v) => set('title', v)} policy="translatable_required" />
          {def.items.withBody && (
            <MultilingualInput label="Item text" value={form.description} multiline
              onChange={(v) => set('description', v)} policy="display_fallback_only" />
          )}
          {def.items.withIcon && (
            <IconPicker value={form.icon} onChange={(v) => set('icon', v)}
                        testid={`v3-item-icon-${item.block_id}`} />
          )}
          {def.items.withImage && (
            <div className="grid grid-cols-2 gap-2">
              <ImageUploader currentUrl={form.thumbnail_url || form.image_url}
                onUploaded={(imgs) => { setForm((f) => ({ ...f, ...imgs })); setDirty(true); }} />
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Input type="number" value={form.display_order}
                 onChange={(e) => set('display_order', parseInt(e.target.value, 10) || 0)}
                 className="h-7 w-16 text-xs" data-testid={`v3-item-order-${item.block_id}`} />
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={busy || !dirty}
                  onClick={() => onSave(item, def, form)}
                  data-testid={`v3-item-save-${item.block_id}`}>
            <Check className="h-4 w-4 text-emerald-600" />
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={busy}
                  onClick={() => onDelete(item)}
                  data-testid={`v3-item-delete-${item.block_id}`}>
            <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── page ──────────────────────────────────────────────────
export default function AdminV3Homepage() {
  const [blocks, setBlocks] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const [b, d] = await Promise.all([
        api.get('/admin/landing-content'),
        api.get('/admin/landing-content/cta-destinations').catch(() => ({ data: {} })),
      ]);
      setBlocks(b.data?.blocks || []);
      setDestinations(d.data?.destinations || []);
    } catch { /* surfaced by the empty state */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const v3 = useMemo(
    () => blocks.filter((b) => (b.homepage_versions || []).includes('V3')), [blocks]);
  const sectionRecord = (key) => (key === 'hero'
    ? v3.find((b) => b.block_type === 'hero_content')
    : v3.find((b) => b.block_type === 'section_copy' && b.section_key === key));
  const sectionItems = (key) => v3
    .filter((b) => b.block_type === 'feature_content' && b.section_key === key && !b.scene_key)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

  const after = async (msg) => { setNote(msg); await load(); setTimeout(() => setNote(''), 4000); };

  const missingCount = V3_SECTIONS.filter((d) => !sectionRecord(d.key)
    || (itemDefaultsFor(d.items?.sectionKey || d.key).length
        && !sectionItems(d.items?.sectionKey || d.key).length)).length;

  const handleCreate = async (def) => {
    setBusy(true);
    try {
      await adoptSection(def);
      await after(`“${def.label}” is now managed from here.`);
    } catch (e) {
      alert(e?.response?.data?.detail?.message || 'Could not start managing this section');
    } finally { setBusy(false); }
  };

  // ONE explicit, idempotent, non-destructive adoption.
  // • creates ONLY what is missing — an existing record is never read, rewritten
  //   or duplicated, so running it twice changes nothing the second time;
  // • copies the exact EN/TR copy the page already renders, so the page looks
  //   identical the moment it finishes;
  // • writes V3-scoped records only, so the published V1 homepage is untouched.
  const handleAdoptAll = async () => {
    const todo = V3_SECTIONS.filter((d) => !sectionRecord(d.key)
      || (itemDefaultsFor(d.items?.sectionKey || d.key).length
          && !sectionItems(d.items?.sectionKey || d.key).length));
    if (!todo.length) return;
    if (!window.confirm(
      `Create the ${todo.length} missing V3 section record(s) from the copy the page `
      + 'already shows?\n\nNothing existing is changed, replaced or deleted, and the '
      + 'live V1 homepage is not touched.')) return;
    setBusy(true);
    let created = 0;
    const failed = [];
    try {
      for (const def of todo) {
        try {
          created += await adoptSection(def);
        } catch (e) {
          failed.push(`${def.label}: ${e?.response?.data?.detail?.message || 'failed'}`);
        }
      }
      await after(failed.length
        ? `${created} record(s) created. Not done: ${failed.join('; ')}`
        : `${created} record(s) created from the existing copy. Nothing else changed.`);
    } finally { setBusy(false); }
  };

  // Creates the section record and its built-in items IF they are missing.
  // Returns how many records it actually created.
  const adoptSection = async (def) => {
    let n = 0;
    if (!sectionRecord(def.key)) {
      const d = defaultsFor(def.key);
      await api.post('/admin/landing-content', {
        block_type: def.key === 'hero' ? 'hero_content' : 'section_copy',
        section_key: def.key === 'hero' ? 'hero' : def.key,
        homepage_versions: ['V3'],
        display_order: def.order,
        active: true,
        title: d.title || { en: '' },
        description: d.description || { en: '' },
        eyebrow: d.eyebrow || '',
        badge_label: d.badge_label || '',
        footnote: d.footnote || '',
        link_label: d.link_label || '',
        cta_label: d.cta_label || '',
        cta_href: d.cta_href || '',
        cta_label_secondary: d.cta_label_secondary || '',
        cta_href_secondary: d.cta_href_secondary || '',
      });
      n += 1;
    }
    const itemKey = def.items?.sectionKey || def.key;
    const itemDefaults = itemDefaultsFor(itemKey);
    if (itemDefaults.length && !sectionItems(itemKey).length) {
      for (let i = 0; i < itemDefaults.length; i += 1) {
        await api.post('/admin/landing-content', {
          block_type: 'feature_content',
          section_key: itemKey,
          homepage_versions: ['V3'],
          active: true,
          display_order: i + 1,
          title: itemDefaults[i].title,
          description: itemDefaults[i].description || { en: '' },
          // Adopting a section keeps the icon the page already shows.
          icon: itemDefaults[i].icon || '',
        });
        n += 1;
      }
    }
    return n;
  };

  const handleSave = async (record, form) => {
    setBusy(true);
    try {
      await api.put(`/admin/landing-content/${record.block_id}`, form);
      await after('Saved.');
    } catch (e) {
      alert(e?.response?.data?.detail?.message || 'Save failed');
    } finally { setBusy(false); }
  };

  const handleToggle = async (record) => {
    setBusy(true);
    try {
      await api.put(`/admin/landing-content/${record.block_id}`,
                    { active: record.active === false });
      await after(record.active === false ? 'Section shown again.' : 'Section hidden on V3.');
    } finally { setBusy(false); }
  };

  const handleItemSave = async (item, def, form) => {
    setBusy(true);
    try {
      if (item) {
        await api.put(`/admin/landing-content/${item.block_id}`, form);
      } else {
        await api.post('/admin/landing-content', {
          block_type: 'feature_content',
          section_key: def.items?.sectionKey || def.key,
          homepage_versions: ['V3'], active: true,
          // A new item starts with a sensible icon so it is never blank.
          icon: def.items?.withIcon ? DEFAULT_ITEM_ICON : '',
          ...form,
          title: form.title?.en ? form.title : { en: 'New item' },
        });
      }
      await after('Saved.');
    } catch (e) {
      alert(e?.response?.data?.detail?.message || 'Save failed');
    } finally { setBusy(false); }
  };

  const handleItemDelete = async (item) => {
    if (!window.confirm(`Delete “${en(item.title) || 'this item'}”?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/landing-content/${item.block_id}`);
      await after('Item deleted.');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="v3-cms-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-v3-homepage">
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs leading-relaxed text-sky-900"
           data-testid="v3-cms-scope-note">
        <span className="font-semibold">This page edits the V3 candidate homepage only.</span>{' '}
        Every record here is scoped to V3, so nothing you change can affect the
        published V1 homepage — V1 content stays on the
        <span className="font-semibold"> Homepage</span> tab. Treatments, providers,
        certificates and provider-benefit copy keep their own authorities and are
        never copied into these records.
      </div>
      {note && (
        <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700"
           data-testid="v3-cms-note">{note}</p>
      )}
      <SectionOrderPanel
        records={Object.fromEntries(V3_SECTIONS.map((d) => [d.key, sectionRecord(d.key)]))}
        onSaved={load} />


      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">
            {missingCount === 0
              ? 'Every V3 section is managed from here'
              : `${missingCount} section${missingCount === 1 ? '' : 's'} still show${missingCount === 1 ? 's' : ''} built-in copy`}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Adoption copies the exact text the page already shows into editable
            records. It creates only what is missing, never overwrites or deletes,
            and can be run again safely.
          </p>
        </div>
        <Button size="sm" disabled={busy || missingCount === 0}
                onClick={handleAdoptAll}
                data-testid="v3-adopt-all">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Plus className="mr-1 h-3.5 w-3.5" />}
          {missingCount === 0 ? 'Nothing to adopt' : `Make all ${missingCount} editable`}
        </Button>
      </div>
      {V3_SECTIONS.map((def) => (
        <SectionCard key={def.key} def={def} record={sectionRecord(def.key)}
                     items={def.items ? sectionItems(def.items.sectionKey || def.key) : null}
                     destinations={destinations} busy={busy}
                     onCreate={handleCreate} onSave={handleSave} onToggle={handleToggle}
                     onItemSave={handleItemSave} onItemDelete={handleItemDelete} />
      ))}
      <p className="pt-2 text-[11px] text-slate-400">
        Section images and product screenshots stay in
        <span className="font-medium"> Homepage → V3 Product Visuals</span>, which keeps the
        approved EN/TR pairing per surface.
      </p>
    </div>
  );
}
